import { ColoursObj, VerticalExpander } from '@eventr/common-storybook'
import { Box, Stack } from '@mui/material'
import { signal, useSignal } from '@preact/signals-react'
import React, { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'
import i18n from '../../i18n/i18next'
import { Paths } from '../../routes'
import { useBoundStore } from '../../state/bound.store'
import { useLanguageContext } from '../../state/languageState/languageContext'
import { useRouteGuardContext } from '../../state/routeGuardProvider'
import {
    keyboardState,
    windowSizes,
} from '../../state/windowState/windowSignals'
import { isWebPreview } from '../../utils'
import { ChatNavBarItem } from './components/chatNavBarItem'
import { NavBarItem } from './components/navBarItem'
import { TeamNavBarItem } from './components/teamNavBarItem'

export const navBarRef = signal(React.createRef<HTMLDivElement>())

const HOME_PATHS = ['/tabs/home$', '/edit(/.*)?$']
const CHAT_PATHS = ['/tabs/rocketchat(/.*)?$']
const TEAM_PATHS = ['/tabs/team(/.*)?$', '/setup/add-users']
const ACCOUNT_PATHS = ['/account(/.*)?$']

const HIDDEN_PATHS = [
    '/$',
    '/authentication(/.*)?$',
    '/edit/',
    '/account/profile/edit',
]

export const activeNavBar = signal<number>(0)
export const navBarHidden = signal<boolean>(false)

export const NavBar = () => {
    const { pathname } = useLocation()
    const { isAllowed } = useRouteGuardContext()

    const taps = useSignal(0)
    const tapsTimer = useSignal<NodeJS.Timeout | null>(null)
    const currPage = window.location.href
    const { selectedLanguage } = useLanguageContext()

    const {
        wpUser,
        noItineraries,
        accessRequests,
        selectedItinEmpty,
        profileRequired,
    } = useBoundStore(
        useShallow((s) => {
            return {
                wpUser: s.wpUser,
                noItineraries: s.noItineraries,
                accessRequests: s.accessRequests,
                selectedItinEmpty: s.selectedItinEmpty,
                profileRequired: s.profileRequired,
            }
        })
    )

    const resoleActiveBasedOnPath = (path: string) => {
        //do regex matching here

        const isHome = HOME_PATHS.some((p) => {
            const regex = new RegExp(`^${p}`, 'i')
            return regex.test(path)
        })
        if (isHome) {
            return 1
        }
        const isChat = CHAT_PATHS.some((p) => {
            const regex = new RegExp(`^${p}`, 'i')
            return regex.test(path)
        })
        if (isChat) {
            return 2
        }
        const isTeam = TEAM_PATHS.some((p) => {
            const regex = new RegExp(`^${p}`, 'i')
            return regex.test(path)
        })
        if (isTeam) {
            return 3
        }
        const isAccount = ACCOUNT_PATHS.some((p) => {
            const regex = new RegExp(`^${p}`, 'i')
            return regex.test(path)
        })
        if (isAccount) {
            return 4
        }
    }

    const resolveHiddenBasedOnPath = (path: string) => {
        if (isWebPreview) {
            return true
        }
        const isHidden = HIDDEN_PATHS.some((p) => {
            const regex = new RegExp(`^${p}`, 'i')
            return regex.test(path)
        })
        return isHidden
    }

    useEffect(() => {
        const act = resoleActiveBasedOnPath(pathname)
        activeNavBar.value = act
        const hid = resolveHiddenBasedOnPath(pathname)

        if (hid) {
            navBarHidden.value = hid
        } else {
            if (
                ['didShow', 'willShow'].includes(keyboardState.value.openState)
            ) {
                navBarHidden.value = true
            } else {
                navBarHidden.value = false
            }
        }
    }, [pathname, keyboardState.value, selectedLanguage])

    const onAccountClick = () => {
        taps.value = taps.peek() + 1
        if (tapsTimer.peek() == null) {
            tapsTimer.value = setTimeout(() => {
                taps.value = 0
                tapsTimer.value = null
            }, 5000)
        }
        if (taps.value === 10) {
            taps.value = 0
            window.pushPath('/devMenu')
        }
    }

    const showAccessRequests = useMemo(() => {
        return wpUser?.roles?.some((role) => {
            return ['administrator', 'eventr_manager', 'owner'].includes(role)
        })
    }, [wpUser])

    const hasAccessRequests = wpUser && showAccessRequests
    const collectiveRequests =
        accessRequests.length + (profileRequired?.length ?? 0)
    const totalRequests = hasAccessRequests
        ? collectiveRequests
        : profileRequired?.length ?? 0

    return (
        <Box
            className="navbar"
            // display={hidden.value ? 'none' : 'block'} removed to work with vertical expander
            zIndex={1000}
            ref={navBarRef.value}
            boxShadow={'0px -4px 5px 0px rgba(16, 55, 92, 0.05)'}
            position={'fixed'}
            bottom={0}
            width={'100%'}
            bgcolor={ColoursObj.backgrounds.navbar}
        >
            <VerticalExpander open={!navBarHidden.value}>
                <Box
                    padding={
                        '0 0 ' + windowSizes.value.insets.insets.bottom + 'px 0'
                    }
                >
                    <Stack
                        padding={'12px 23px 12px 23px'}
                        direction={'row'}
                        justifyContent={'space-between'}
                    >
                        <NavBarItem
                            id="navbarItemHome"
                            label={i18n.t('navbar.home')}
                            active={activeNavBar.value === 1}
                            path={Paths.home}
                            icon={'Home'}
                            order={0}
                            pulse={
                                isAllowed(['allowedEdit']) &&
                                noItineraries &&
                                (currPage.endsWith('team') ||
                                    currPage.endsWith('rocketchat'))
                            }
                        />

                        <ChatNavBarItem />

                        <TeamNavBarItem />

                        <Box onClick={onAccountClick}>
                            <NavBarItem
                                order={3}
                                id="navbarItemAccount"
                                label={i18n.t('navbar.account')}
                                active={activeNavBar.value === 4}
                                path="/account"
                                icon={'Settings'}
                                pulse={
                                    !isAllowed(['allowedEdit']) &&
                                    selectedItinEmpty
                                }
                                count={totalRequests}
                            />
                        </Box>
                    </Stack>
                </Box>
            </VerticalExpander>
        </Box>
    )
}
